import {
  contrastRatio,
  reportNumberValue,
  type ReportTone,
  type TableConditionalFormat,
} from '@erp/shared-types';
import type {
  ReportDatasetRow,
  ReportDatasetValue,
} from '../ReportDataContext';

/** DESIGN.md's status signals (the badge clusters' tinted fill and text). */
export const TONE_STYLES: Readonly<
  Record<ReportTone, { backgroundColor: string; color: string }>
> = {
  positive: { backgroundColor: '#ecfdf5', color: '#166534' },
  negative: { backgroundColor: '#fef2f2', color: '#991b1b' },
  warning: { backgroundColor: '#fffbeb', color: '#92400e' },
  neutral: { backgroundColor: '#f5f5f4', color: '#292524' },
};

const INK = '#1c1917';
const WHITE = '#ffffff';

export type CellStyle = { backgroundColor: string; color: string };

type ThresholdRule = Extract<TableConditionalFormat, { kind: 'threshold' }>;

/** Whether `n` sits between two bounds, given in either order, inclusive. */
function between(n: number, a: number, b: number): boolean {
  return n >= Math.min(a, b) && n <= Math.max(a, b);
}

const THRESHOLD_TESTS: Readonly<
  Record<ThresholdRule['op'], (n: number, rule: ThresholdRule) => boolean>
> = {
  gt: (n, rule) => n > rule.value,
  gte: (n, rule) => n >= rule.value,
  lt: (n, rule) => n < rule.value,
  lte: (n, rule) => n <= rule.value,
  eq: (n, rule) => n === rule.value,
  neq: (n, rule) => n !== rule.value,
  between: (n, rule) => between(n, rule.value, rule.value2 ?? rule.value),
};

function passes(rule: ThresholdRule, n: number): boolean {
  return THRESHOLD_TESTS[rule.op](n, rule);
}

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function rgbToHex(rgb: readonly number[]): string {
  return `#${rgb
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** The color `t` (0 to 1) of the way along a ramp, between its stops. */
export function rampColor(ramp: readonly string[], t: number): string {
  if (!ramp.length) return WHITE;
  const clamped = Math.min(1, Math.max(0, t));
  const position = clamped * (ramp.length - 1);
  const index = Math.min(ramp.length - 2, Math.floor(position));
  if (index < 0) return ramp[0] ?? WHITE;
  const from = hexToRgb(ramp[index] ?? WHITE);
  const to = hexToRgb(ramp[index + 1] ?? WHITE);
  const local = position - index;
  return rgbToHex(
    from.map((channel, i) => channel + ((to[i] ?? 0) - channel) * local),
  );
}

/** Ink on a light fill, white on a dark one: whichever reads better. */
export function readableText(background: string): string {
  return contrastRatio(background, INK) >= contrastRatio(background, WHITE)
    ? INK
    : WHITE;
}

/** A column's least and greatest Number among the rows; null when it has none. */
function numberRange(
  rows: readonly ReportDatasetRow[],
  column: string,
): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const row of rows) {
    const n = reportNumberValue(row[column]);
    if (n === null) continue;
    min = Math.min(min, n);
    max = Math.max(max, n);
  }
  return min <= max ? { min, max } : null;
}

/** Each color-scaled column's least and greatest Number among the shown rows. */
export function colorScaleRanges(
  formats: readonly TableConditionalFormat[],
  rows: readonly ReportDatasetRow[],
): Map<string, { min: number; max: number }> {
  const ranges = new Map<string, { min: number; max: number }>();
  for (const format of formats) {
    if (format.kind !== 'color-scale' || ranges.has(format.column)) continue;
    const range = numberRange(rows, format.column);
    if (range) ranges.set(format.column, range);
  }
  return ranges;
}

/**
 * How one cell is painted by a Table's conditional formats: its place on a
 * color scale (the sequential ramp between the shown rows' least and greatest
 * value), unless a threshold rule matches it — a rule is the exception a
 * scale is the backdrop for, so it wins whatever the order; of several
 * matching rules the later wins. Null when none applies; a cell that isn't a
 * Number is never painted.
 */
export function conditionalCellStyle(
  formats: readonly TableConditionalFormat[],
  column: string,
  value: ReportDatasetValue | undefined,
  ranges: ReadonlyMap<string, { min: number; max: number }>,
  sequential: readonly string[],
): CellStyle | null {
  const n = reportNumberValue(value);
  if (n === null) return null;
  const own = formats.filter((format) => format.column === column);
  const rule = own.findLast(
    (format) => format.kind === 'threshold' && passes(format, n),
  );
  if (rule?.kind === 'threshold') return TONE_STYLES[rule.tone];
  const range = ranges.get(column);
  if (!range || !own.some((format) => format.kind === 'color-scale'))
    return null;
  const t =
    range.max === range.min ? 1 : (n - range.min) / (range.max - range.min);
  const backgroundColor = rampColor(sequential, t);
  return { backgroundColor, color: readableText(backgroundColor) };
}
